
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RefreshCw, Copy, Check, Edit } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface PasswordGeneratorProps {
  onPasswordChange: (password: string) => void;
  initialPassword?: string;
}

const PasswordGenerator: React.FC<PasswordGeneratorProps> = ({ 
  onPasswordChange, 
  initialPassword = '' 
}) => {
  const [password, setPassword] = useState(initialPassword || generatePassword());
  const [copied, setCopied] = useState(false);
  const [isManualMode, setIsManualMode] = useState(false);
  const { toast } = useToast();

  function generatePassword(): string {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  }

  const handleGenerateNew = () => {
    const newPassword = generatePassword();
    setPassword(newPassword);
    onPasswordChange(newPassword);
    setCopied(false);
    setIsManualMode(false);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    onPasswordChange(newPassword);
    setCopied(false);
  };

  const toggleManualMode = () => {
    setIsManualMode(!isManualMode);
    if (!isManualMode) {
      // Switching to manual mode, clear the password so user can type
      setPassword('');
      onPasswordChange('');
    } else {
      // Switching back to generated mode, generate a new password
      handleGenerateNew();
    }
    setCopied(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
      toast({
        title: 'Password copied',
        description: 'Password has been copied to clipboard',
      });
      setTimeout(() => setCopied(false), 3000);
    } catch (error) {
      toast({
        title: 'Copy failed',
        description: 'Failed to copy password to clipboard',
        variant: 'destructive',
      });
    }
  };

  React.useEffect(() => {
    if (!initialPassword) {
      onPasswordChange(password);
    }
  }, []);

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <Label htmlFor="generated-password">Password</Label>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={toggleManualMode}
          className="text-xs"
        >
          <Edit className="h-3 w-3 mr-1" />
          {isManualMode ? 'Use Generator' : 'Manual Input'}
        </Button>
      </div>
      <div className="flex gap-2">
        <Input
          id="generated-password"
          type="text"
          value={password}
          onChange={handlePasswordChange}
          className={isManualMode ? "" : "font-mono"}
          placeholder={isManualMode ? "Enter password manually" : "Generated password will appear here"}
          readOnly={!isManualMode}
        />
        {!isManualMode && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleGenerateNew}
            title="Generate new password"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          size="icon"
          onClick={copyToClipboard}
          title="Copy to clipboard"
          disabled={!password}
        >
          {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground">
        {isManualMode 
          ? "Enter a custom password for the author's account."
          : "This password will be used for the author's account. Make sure to share it with them securely."
        }
      </p>
    </div>
  );
};

export default PasswordGenerator;
